import { mapYupErrorsToErrorMap, useNotifier } from "@repo/shared/common";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { createInvoicePaymentSchema } from "../types/InvoiceValidation";

import type { ErrorMap } from "@repo/shared/common";
import type { TFunction } from "next-i18next";
import type * as Yup from "yup";
import { PayData } from "../dialogs/PaymentRecordForwardRefDialog";

export function useValidator<TIn extends object>(
	createSchema: (t: TFunction) => Yup.ObjectSchema<NonNullable<TIn>>,
) {
	const { t } = useTranslation();
	const [errors, setErrors] = useState<ErrorMap>({});
	const { notifyFailure } = useNotifier();

	// Memoize schema to avoid recreating it on every render
	const schema = useMemo(() => createSchema(t), [createSchema, t]);

	// Memoize validate function to maintain stable reference
	const validate = useCallback(
		async (dataModel: NonNullable<TIn>): Promise<{ isValid: boolean; errors: ErrorMap }> => {
			try {
				await schema.validate(dataModel, { abortEarly: false });

				// No errors from schema validation
				const emptyErrors = {};
				setErrors(emptyErrors);
				return { isValid: true, errors: emptyErrors };
			} catch (err) {
				notifyFailure(t("validation-failed-plz-resolve-errors"));

				const errorMap = mapYupErrorsToErrorMap(err);
				setErrors(errorMap);
				return { isValid: false, errors: errorMap };
			}
		},
		[schema, notifyFailure, t],
	);

	return { errors, setErrors, validate };
}

export function useInvoicePaymentValidator(remainingAmount?: number) {
	const { t } = useTranslation();
	const [errors, setErrors] = useState<ErrorMap>({});
	const { notifyFailure } = useNotifier();

	// Memoize schema with remaining amount constraint
	const schema = useMemo(
		() => createInvoicePaymentSchema(t, remainingAmount),
		[t, remainingAmount],
	);

	// Memoize validate function to maintain stable reference
	const validate = useCallback(
		async (dataModel: NonNullable<PayData>): Promise<{ isValid: boolean; errors: ErrorMap }> => {
			try {
				await schema.validate(dataModel, { abortEarly: false });

				const emptyErrors = {};
				setErrors(emptyErrors);
				return { isValid: true, errors: emptyErrors };
			} catch (err) {
				notifyFailure(t("validation-failed-plz-resolve-errors"));

				const errorMap = mapYupErrorsToErrorMap(err);
				setErrors(errorMap);
				return { isValid: false, errors: errorMap };
			}
		},
		[schema, notifyFailure, t],
	);

	return { errors, setErrors, validate };
}
